import { useCallback, useRef, useState } from 'react'
import { splitBulkUploadPick } from '../utils/bulkUploadFiles'
import { apiErrorMessage } from '../utils/apiErrors'

// Pick-time gate: mp3/m4a only, 1-50 files. `accept` can't be trusted (advisory,
// and audio/mp4 leaks .mp4 on macOS), so validate by extension here. Unsupported
// files are skipped per file (good ones still upload, message names the skips);
// only the count limit blocks the whole pick — silently uploading 50 of 60 would
// hide which files were dropped. Re-exported here for the existing hook tests
// (BulkUpload.js can't be imported in Jest — ESM node_modules).
export const splitPick = splitBulkUploadPick

// Orchestrates a bulk upload batch: one presign for all files -> concurrent PUTs
// with per-file progress -> one create for the successes (staggered dates; published
// unless isDraft, which creates every episode in the batch as a Draft Episode).
// api and transport are injected so the state machine is testable without network.
export const runBulkUpload = async ({ files, showId, isDraft = false, api, transport, onState = () => {}, onItems = () => {}, isCancelled = () => false, now = () => new Date() }) => {
  // Per-file rows, index-aligned with `files` and the presign response.
  const items = files.map((f) => ({ name: f.name, progress: 0, status: 'pending' }))
  const patch = (i, next) => { items[i] = { ...items[i], ...next }; onItems([...items]) }

  let stage = 'presigning'
  try {
    onState('presigning')
    onItems([...items])
    const checksums = await Promise.all(files.map((f) => transport.checksum(f)))
    const blobs = await api.presign(showId, files.map((f, i) => ({
      filename: f.name,
      byte_size: checksums[i].byteSize,
      checksum: checksums[i].checksum,
      content_type: checksums[i].contentType
    })))

    stage = 'uploading'
    onState('uploading')
    files.forEach((_, i) => patch(i, { status: 'uploading' }))
    // Per-file success booleans via Promise.all — Promise.allSettled is unpolyfilled
    // in this CRA 3 app, and the handlers already know each outcome anyway.
    const results = await Promise.all(files.map((file, i) =>
      transport.put({
        url: blobs[i].url,
        headers: blobs[i].headers,
        file,
        onProgress: (fraction) => patch(i, { progress: fraction })
      }).then(
        () => { patch(i, { status: 'done', progress: 1 }); return true },
        (error) => {
          patch(i, { status: 'error', error: (error && error.message) || 'Upload failed' })
          return false
        }
      )))

    // User confirmed the nav-away prompt mid-batch: the page is gone, so never create
    // episodes behind their back — that's what the prompt promised.
    // ponytail: in-flight PUTs aren't aborted; without a create, orphan blobs are harmless.
    if (isCancelled()) return []

    // Build the create payload from successful uploads only, in selection order.
    // Indices of successful uploads, in selection order.
    const successIdx = files.map((_, i) => i).filter((i) => results[i])

    // Settled-means-fire: if every upload failed there's nothing to create, but the
    // batch still completed — end in 'done' so the results modal reports 0 published.
    if (successIdx.length === 0) {
      onState('done')
      return []
    }

    // audio_date staggered one second apart so a newest-first feed lists them in
    // pick order: first pick = now (top), each later pick one second older. Never
    // future-dated (a future audio_date reads as a scheduled release and hides).
    const base = now().getTime()
    const audioposts = successIdx.map((i, pos) => ({
      signed_id: blobs[i].signed_id,
      name: stripExtension(files[i].name),
      audio_date: new Date(base - pos * 1000).toISOString(),
      is_draft: isDraft
    }))

    stage = 'creating'
    onState('creating')
    const episodes = await api.create(showId, audioposts)

    // The create endpoint is best-effort per item: an unresolvable signed_id or a
    // rejected episode comes back as status:'failed' rather than throwing. Flip those
    // rows to 'error' so they join the upload failures in the results modal.
    const idxBySignedId = new Map(blobs.map((b, i) => [b.signed_id, i]))
    episodes.forEach((ep) => {
      if (ep.status === 'failed' && idxBySignedId.has(ep.signed_id)) {
        patch(idxBySignedId.get(ep.signed_id), {
          status: 'error',
          error: apiErrorMessage(ep) || 'Episode could not be created'
        })
      }
    })

    onState('done')
    return episodes
  } catch (err) {
    onState('error')
    const failure = err instanceof Error ? err : new Error(String(err || 'Bulk upload failed'))
    if (!failure.stage) failure.stage = stage
    throw failure
  }
}

const stripExtension = (filename) => filename.replace(/\.[^./\\]+$/, '')

// True while a batch is in flight (uploads or the create call running). The picker
// stays locked for exactly these states.
export const isBatchInFlight = (status) =>
  status !== 'idle' && status !== 'done' && status !== 'error'

// True while leaving the page can still prevent episodes from being created. Once
// the create POST is dispatched ('creating') the batch is committed either way, so
// the nav guards arm off this, not isBatchInFlight — prompting then would be a lie.
export const isCancellable = (status) =>
  status === 'presigning' || status === 'uploading'

// Thin React wrapper: exposes the machine's status/items/episodes/error and a start(files) trigger.
export const useBulkUpload = ({ showId, api, transport }) => {
  const [status, setStatus] = useState('idle')
  const [items, setItems] = useState([])
  const [episodes, setEpisodes] = useState(null)
  const [error, setError] = useState(null)
  // Flipped by cancel() when the screen unmounts mid-batch: blocks the create call
  // (no episodes behind the user's back) and silences state updates after unmount.
  const cancelledRef = useRef(false)

  const start = useCallback((files, isDraft = false) => {
    setError(null)
    setEpisodes(null)
    setItems([])
    const live = (set) => (v) => { if (!cancelledRef.current) set(v) }
    return runBulkUpload({
      files, showId, isDraft, api, transport,
      onState: live(setStatus), onItems: live(setItems),
      isCancelled: () => cancelledRef.current
    })
      .then(live(setEpisodes))
      .catch(live(setError))
  }, [showId, api, transport])

  const cancel = useCallback(() => { cancelledRef.current = true }, [])

  // Clear back to an empty, unlocked picker for the next batch ("Upload more").
  const reset = useCallback(() => {
    setStatus('idle')
    setItems([])
    setEpisodes(null)
    setError(null)
  }, [])

  // Picker stays locked from first upload until the batch's create call completes.
  const busy = isBatchInFlight(status)
  // Nav guards arm off this narrower window (see isCancellable).
  const cancellable = isCancellable(status)

  return { status, items, episodes, error, busy, cancellable, start, reset, cancel }
}
